import { type Menu, RouteLoaderEnum, StateNames } from '@/admin/types/menu'
import { VoiceProvider } from '@/common/types/cti'
import {
  faArrowDownToBracket,
  faArrowRightArrowLeft,
  faArrowUpFromBracket,
  faFolders,
  faGear,
  faMagnifyingGlass,
  faSliders,
  faSparkles,
  faStar,
  faShapes,
  faUserHeadset,
  faUsers,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons'
import { ADMIN_ROUTE_PATHS } from '@/admin/types/router'
import { AuthoritiesEnum } from '@/common/types/session'

const menu: Menu = [
  {
    name: 'assist.admin.menu.category.users',
    icon: faUserHeadset,
    children: [
      {
        name: 'assist.admin.menu.entry.profiles',
        accessRights: [AuthoritiesEnum.ADMIN_USERPROFILES],
        link: '/assist/admin/userprofiles/',
      },
      {
        name: 'assist.admin.menu.entry.teams',
        accessRights: [AuthoritiesEnum.ADMIN_USERGROUPS],
        link: '/assist/admin/groups/',
      },
      {
        name: 'assist.admin.menu.entry.users',
        accessRights: [AuthoritiesEnum.ADMIN_USERS],
        link: '/assist/admin/users/',
      },
      {
        name: 'assist.admin.menu.entry.audit',
        accessRights: [AuthoritiesEnum.ADMIN_AUTHENTICATION],
        link: '/assist/admin/audit/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.operations',
    icon: faUsers,
    children: [
      {
        name: 'assist.admin.menu.entry.spaces',
        accessRights: [AuthoritiesEnum.ADMIN_SKILLGROUPS],
        link: '/assist/admin/skillGroups/',
      },
      {
        name: 'assist.admin.menu.entry.skills',
        accessRights: [AuthoritiesEnum.ADMIN_SKILLS],
        link: '/assist/admin/skills/',
      },
      {
        name: 'assist.admin.menu.entry.screens',
        accessRights: [AuthoritiesEnum.ADMIN_SCREENS],
        link: '/assist/admin/screens/',
      },
      {
        name: 'assist.admin.menu.entry.transfer.rules',
        accessRights: [AuthoritiesEnum.ADMIN_SKILLS],
        link: '/assist/admin/skills/transferRules',
      },
      {
        name: 'assist.admin.menu.entry.schedules',
        accessRights: [AuthoritiesEnum.ADMIN_SCHEDULE],
        link: '/assist/admin/schedules/',
      },
      {
        name: 'assist.admin.menu.entry.holidays',
        accessRights: [AuthoritiesEnum.ADMIN_SCHEDULE],
        link: '/assist/admin/holiday/',
      },
      {
        name: 'assist.admin.menu.entry.breaks',
        accessRights: [AuthoritiesEnum.ADMIN_PAUSE_TYPES],
        link: '/assist/admin/breaks/',
      },
      {
        name: 'assist.admin.menu.entry.activities',
        accessRights: [AuthoritiesEnum.ADMIN_ACTIVITIES],
        link: '/assist/admin/activities',
      },
      {
        name: 'assist.admin.menu.entry.templates',
        accessRights: [AuthoritiesEnum.ADMIN_CANNED_RESPONSES],
        link: '/assist/admin/cannedResponses/',
      },
      {
        name: 'assist.admin.menu.entry.customfields',
        accessRights: [AuthoritiesEnum.ADMIN_CUSTOM_FIELDS],
        link: '/assist/admin/customfields/',
      },
      {
        name: 'assist.admin.menu.entry.integrationPointCards',
        accessRights: [AuthoritiesEnum.ADMIN_CUSTOM_FIELDS],
        link: ADMIN_ROUTE_PATHS.INTEGRATION_POINT_CARDS,
      },
      {
        name: 'assist.admin.menu.category.contacts',
        children: [
          {
            name: 'assist.admin.menu.entry.addressbook',
            accessRight: AuthoritiesEnum.ADMIN_ADDRESSBOOK,
            link: '/assist/admin/addressBooks/',
          },
          {
            name: 'assist.admin.menu.entry.contact.lists',
            accessRight: AuthoritiesEnum.ADMIN_CONTACT_LISTS,
            link: '/assist/admin/contacts/',
          },
        ],
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.channels',
    icon: faSliders,
    children: [
      {
        name: 'assist.admin.menu.entry.channels',
        accessRights: [AuthoritiesEnum.ADMIN_CHANNELS],
        link: '/assist/admin/channels/',
      },
      {
        name: 'assist.admin.menu.category.voice',
        loader: RouteLoaderEnum.CTI_LOADER,
        children: [
          {
            name: 'Axialys',
            accessRight: AuthoritiesEnum.ADMIN_CTI,
            link: '/assist/admin/voice/axialys-configuration/',
            stateName: StateNames.CTI_PROVIDER,
            state: VoiceProvider.AXIALYS,
          },
          {
            name: 'Genesys',
            accessRight: AuthoritiesEnum.ADMIN_CTI,
            link: '/assist/admin/voice/genesys-configuration/',
            stateName: StateNames.CTI_PROVIDER,
            state: `${VoiceProvider.GENESYS}`,
          },
          {
            name: 'Unified CTI',
            accessRight: AuthoritiesEnum.ADMIN_CTI,
            link: '/assist/admin/voice/unified-cti-configuration/',
            stateName: StateNames.CTI_PROVIDER,
            state: `${VoiceProvider.UNIFIED_CTI}`,
          },
          {
            name: 'Five 9',
            accessRight: AuthoritiesEnum.ADMIN_CTI,
            link: '/assist/admin/voice/five9-configuration/',
            stateName: StateNames.CTI_PROVIDER,
            state: `${VoiceProvider.FIVE_9}`,
          },
          {
            name: 'Voicebot',
            accessRight: AuthoritiesEnum.ADMIN_CTI,
            link: '/assist/admin/voice/voicebot-configuration/',
            stateName: StateNames.CTI_PROVIDER,
            state: `${VoiceProvider.VOICEBOT}`,
            alwaysActive: true,
          },
        ],
      },
      {
        name: 'assist.admin.menu.category.email',
        children: [
          {
            name: 'assist.admin.menu.entry.mailboxes',
            accessRight: AuthoritiesEnum.ADMIN_MAIL_MAILBOX,
            link: '/assist/admin/mail/mailboxes/',
          },
          {
            name: 'assist.admin.menu.entry.smtp.servers',
            accessRight: AuthoritiesEnum.ADMIN_MAIL_SMTP,
            link: '/assist/admin/mail/smtp/',
          },
          {
            name: 'assist.admin.menu.entry.envelopes',
            accessRight: AuthoritiesEnum.ADMIN_MAIL_ENVELOPE,
            link: '/assist/admin/mail/envelopes/',
          },
          {
            name: 'assist.admin.menu.entry.deliveryreceipts',
            accessRight: AuthoritiesEnum.ADMIN_MAIL_ACKNOWLEDGMENTS,
            link: '/assist/admin/mail/acknowledgments/',
          },
          {
            name: 'assist.admin.menu.entry.email.routing.rules',
            accessRight: AuthoritiesEnum.ADMIN_RULES,
            link: '/assist/admin/mail/routing/',
          },
          {
            name: 'assist.admin.menu.entry.mail.storage.settings',
            accessRight: AuthoritiesEnum.ADMIN_SYSTEM_PARAM,
            link: ADMIN_ROUTE_PATHS.MAIL_STORAGE_SETTINGS,
          },
          {
            name: 'assist.admin.menu.entry.mail.job.configuration',
            accessRight: AuthoritiesEnum.ADMIN_SYSTEM_PARAM,
            link: ADMIN_ROUTE_PATHS.IN_TRANSIT_MAIL_JOB_CONFIGURATION,
          },
        ],
      },
      {
        name: 'assist.admin.menu.category.sms',
        children: [
          {
            name: 'assist.admin.menu.entry.sms.providers',
            accessRight: AuthoritiesEnum.ADMIN_SMS_PROVIDER,
            link: '/assist/admin/sms/providers/',
          },
          {
            name: 'assist.admin.menu.entry.sms.senders',
            accessRight: AuthoritiesEnum.ADMIN_SMS_SENDER,
            link: '/assist/admin/sms/senders/',
          },
          {
            name: 'assist.admin.menu.entry.vonage',
            accessRight: AuthoritiesEnum.ADMIN_SMS_SENDER,
            link: '/assist/admin/vonage/configurations',
          },
          {
            name: 'assist.admin.menu.entry.outbound.messages',
            accessRight: AuthoritiesEnum.ADMIN_SMS_SENDER,
            link: '/assist/admin/outbound-messages/list',
          },
        ],
      },
      {
        name: 'assist.admin.menu.category.social',
        children: [
          {
            name: 'assist.admin.menu.entry.whatsapp',
            accessRight: AuthoritiesEnum.ADMIN_SOCIAL_WHATSAPP,
            link: '/assist/admin/social/whatsApp/',
          },
        ],
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.cases',
    icon: faFolders,
    children: [
      {
        name: 'assist.admin.menu.entry.search.cases.results',
        accessRights: [AuthoritiesEnum.ADMIN_TICKETS_LISTING_SCREEN],
        link: '/assist/admin/tickets/list',
      },
      {
        name: 'assist.admin.menu.entry.contactdrivers',
        accessRights: [AuthoritiesEnum.ADMIN_TYPOLOGIES],
        link: '/assist/admin/typologies/',
      },
      {
        name: 'assist.admin.menu.entry.reasons',
        accessRights: [AuthoritiesEnum.ADMIN_REASONS],
        link: '/assist/admin/reasons',
      },
      {
        name: 'assist.admin.menu.entry.priorities',
        accessRights: [AuthoritiesEnum.ADMIN_PRIORITIES],
        link: '/assist/admin/priority/',
      },
      {
        name: 'assist.admin.menu.entry.emergencies',
        accessRights: [AuthoritiesEnum.ADMIN_EMERGENCIES],
        link: '/assist/admin/emergency/',
      },
      {
        name: 'assist.admin.menu.entry.products',
        accessRights: [AuthoritiesEnum.ADMIN_PRODUCTS],
        link: '/assist/admin/products/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.entities',
    icon: faShapes,
    children: [
      {
        name: 'assist.admin.menu.category.company',
        children: [
          {
            name: 'assist.admin.menu.entry.company.record',
            accessRight: AuthoritiesEnum.COMPANY_RECORD,
            link: '/assist/admin/entityRecord/',
          },
          {
            name: 'assist.admin.menu.entry.company.list',
            accessRight: AuthoritiesEnum.ADMIN_COMPANIES_LISTING_SCREEN,
            link: '/assist/admin/entities/list',
          },
          {
            name: 'assist.admin.company.entity.settings.title',
            accessRight: AuthoritiesEnum.COMPANY_ENTITIES_SETTINGS,
            link: ADMIN_ROUTE_PATHS.ENTITIES_SETTINGS,
          },
        ],
      },
      {
        name: 'assist.admin.menu.category.customers',
        children: [
          {
            name: 'assist.admin.menu.entry.customer.record',
            accessRight: AuthoritiesEnum.ADMIN_SCREENS_CUSTOMER,
            link: '/assist/admin/screensCustomer/',
          },
          {
            name: 'assist.admin.menu.entry.search.customer.results',
            accessRight: AuthoritiesEnum.ADMIN_CLIENTS_LISTING_SCREEN,
            link: '/assist/admin/customers/list',
          },
          {
            name: 'assist.admin.menu.entry.external.referential.customer.settings',
            accessRight: AuthoritiesEnum.ADMIN_SYSTEM_PARAM,
            link: ADMIN_ROUTE_PATHS.EXTERNAL_REFERENTIAL_CUSTOMER_MS,
          },
        ],
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.interactions',
    icon: faArrowRightArrowLeft,
    children: [
      {
        name: 'assist.admin.menu.entry.interactions.list',
        accessRights: [AuthoritiesEnum.ADMIN_INTERACTIONS_LISTING_SCREEN],
        link: '/assist/admin/interactions/list',
      },
      {
        name: 'assist.admin.menu.entry.interactions.customtypes',
        accessRights: [AuthoritiesEnum.ADMIN_EXTERNAL_INTERACTION_TYPES],
        link: '/assist/admin/interactions/custom-types/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.search',
    icon: faMagnifyingGlass,
    children: [
      {
        name: 'assist.admin.menu.entry.search.config',
        accessRights: [AuthoritiesEnum.ADMIN_SEARCH_CRITERIA],
        link: '/assist/admin/search',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.export',
    icon: faArrowUpFromBracket,
    children: [
      {
        name: 'assist.admin.menu.entry.export.tickets',
        accessRights: [AuthoritiesEnum.ADMIN_TICKET_EXPORT_DEFINITION],
        link: '/assist/admin/tickets/export',
      },
      {
        name: 'assist.admin.menu.entry.export.customers',
        accessRights: [AuthoritiesEnum.ADMIN_CUSTOMER_EXPORT_DEFINITION],
        link: '/assist/admin/customers/export',
      },
      {
        name: 'assist.admin.menu.entry.export.interactions',
        accessRights: [AuthoritiesEnum.ADMIN_INTERACTION_EXPORT_DEFINITION],
        link: '/assist/admin/interactions/export',
      },
      {
        name: 'assist.admin.menu.entry.export.companies',
        accessRights: [AuthoritiesEnum.ADMIN_COMPANY_EXPORT_DEFINITION],
        link: '/assist/admin/companies/export',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.import',
    icon: faArrowDownToBracket,
    children: [
      {
        name: 'assist.admin.menu.entry.import.files',
        accessRights: [AuthoritiesEnum.ADMIN_FILE_DEFINITIONS],
        link: '/assist/admin/fileDefinitions/',
      },
      {
        name: 'assist.admin.menu.entry.import.rules',
        accessRights: [AuthoritiesEnum.ADMIN_FILE_RULES],
        link: '/assist/admin/fileRules/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.automation',
    icon: faWandMagicSparkles,
    children: [
      {
        name: 'assist.admin.menu.entry.jobs',
        accessRights: [AuthoritiesEnum.ADMIN_SYSTEM_PARAM],
        link: '/assist/admin/quartzjobs/',
      },
      {
        name: 'assist.admin.menu.entry.triggers',
        accessRights: [AuthoritiesEnum.ADMIN_TRIGGER],
        link: '/assist/admin/trigger/',
      },
      {
        name: 'assist.admin.menu.entry.notifications',
        accessRights: [AuthoritiesEnum.ADMIN_NOTIFICATIONS],
        link: '/assist/admin/notifications/',
      },
      {
        name: 'assist.admin.menu.entry.archiving',
        accessRights: [AuthoritiesEnum.ADMIN_TICKET_ARCHIVING],
        link: '/assist/admin/ticketArchiving/',
      },
      {
        name: 'assist.admin.menu.entry.distributionrules',
        accessRights: [AuthoritiesEnum.ADMIN_RULES],
        link: '/assist/admin/rules/',
      },
      {
        name: 'assist.admin.menu.entry.jobslogs',
        accessRights: [
          AuthoritiesEnum.ADMIN_SYSTEM_PARAM,
          AuthoritiesEnum.ADMIN_TRIGGER,
          AuthoritiesEnum.ADMIN_NOTIFICATIONS,
          AuthoritiesEnum.ADMIN_TICKET_ARCHIVING,
          AuthoritiesEnum.ADMIN_QUALITY_GRID,
          AuthoritiesEnum.ADMIN_SATISFACTION,
        ],
        link: '/assist/admin/joblogs/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.performance',
    icon: faStar,
    children: [
      {
        name: 'assist.admin.menu.category.satisfaction',
        children: [
          {
            name: 'assist.admin.menu.entry.surveys',
            accessRight: AuthoritiesEnum.ADMIN_SATISFACTION_SURVEY,
            link: '/assist/admin/satisfaction/surveys',
          },
          {
            name: 'assist.admin.menu.entry.campaigns',
            accessRight: AuthoritiesEnum.ADMIN_SATISFACTION,
            link: '/assist/admin/satisfaction/campaigns',
          },
        ],
      },
      {
        name: 'assist.admin.menu.entry.quality',
        accessRights: [AuthoritiesEnum.ADMIN_QUALITY_GRID],
        link: '/assist/admin/quality/grids',
      },
      {
        name: 'assist.admin.menu.entry.scripts',
        accessRights: [AuthoritiesEnum.ADMIN_SATISFACTION_SCRIPT],
        link: '/assist/admin/satisfaction/scripts/',
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.ai.features',
    icon: faSparkles,
    children: [
      {
        name: 'assist.admin.menu.entry.ai.general.configuration',
        accessRights: [AuthoritiesEnum.ADMIN_SYSTEM_PARAM],
        link: '/assist/admin/ai/',
      },
      {
        name: 'assist.admin.menu.entry.ai.summarize',
        accessRights: [AuthoritiesEnum.ADMIN_SCREENS_CUSTOMER],
        link: ADMIN_ROUTE_PATHS.ADMIN_AI_SUMMARIZE,
      },
      {
        name: 'assist.admin.menu.entry.ai.automatic.resolution.notes',
        accessRights: [AuthoritiesEnum.ADMIN_SYSTEM_PARAM],
        link: ADMIN_ROUTE_PATHS.AUTOMATIC_RESOLUTION_NOTES,
      },
    ],
  },
  {
    name: 'assist.admin.menu.category.settings',
    icon: faGear,
    children: [
      {
        name: 'assist.admin.menu.entry.settings.global',
        accessRights: [AuthoritiesEnum.ADMIN_SYSTEM_PARAM],
        link: '/assist/admin/params/',
      },
      {
        name: 'assist.admin.menu.entry.settings.regional',
        accessRights: [AuthoritiesEnum.ADMIN_REGIONAL_SETTINGS],
        link: '/assist/admin/regional/',
      },
      {
        name: 'assist.admin.menu.entry.customerids',
        accessRights: [AuthoritiesEnum.ADMIN_SYSTEM_PARAM],
        link: '/assist/admin/settings/customer/view',
      },
      {
        name: 'assist.admin.menu.entry.translations',
        accessRights: [AuthoritiesEnum.ADMIN_TRANSLATION],
        link: '/assist/admin/trans/',
      },
      {
        name: 'assist.admin.menu.entry.resources',
        accessRights: [AuthoritiesEnum.ADMIN_RESOURCES],
        link: '/assist/admin/resources/',
      },
      {
        name: 'assist.admin.menu.entry.workflow.transitions',
        accessRights: [AuthoritiesEnum.ADMIN_WORKFLOW_TRANSITIONS],
        link: '/assist/admin/wkfwTransition/',
      },
      {
        name: 'assist.admin.menu.entry.lab',
        accessRights: [AuthoritiesEnum.ADMIN_INNSO_LAB],
        link: '/assist/admin/lab/',
      },
      {
        name: 'assist.admin.menu.entry.logos',
        accessRights: [AuthoritiesEnum.ADMIN_LOGOS],
        link: '/assist/admin/logo/',
      },
      {
        name: 'assist.admin.menu.entry.versions',
        accessRights: [AuthoritiesEnum.ADMIN_INNSO_VERSIONS],
        link: '/assist/admin/versions/',
      },
    ],
  },
]

export default menu
